import { useFormik } from "formik";
import apiRequest from "../../../utils/admin/apiRequest";
import InputField from "../../ui/form/InputField";
import Button from "../../ui/utils/Button";
import SelectField from "../../ui/form/SelectField";
import Alert from "../../ui/utils/Alert";
import useFetch from "../../../hooks/api/useFetch";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import FileInputField from "../../ui/form/FileInputField";
import axios from "axios";
import authManager from "../../../utils/admin/authManager";

const ProductImport = ({ closeModal, callback }) => {
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(0);
  const [serverError, setServerError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      mainCategory: "",
      // parentCategory: "",
      // subCategory: "",
      file: null,
      usertype: "admin",
      userId: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      mainCategory: Yup.string().required("Main Category is required"),
      file: Yup.mixed()
        .required("Excel file is required")
        .test("fileType", "Only .xlsx or .xls files allowed", (value) =>
          value
            ? [
                "application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              ].includes(value.type)
            : false
        ),
    }),
    onSubmit: async (values, { resetForm }) => {
      setServerError("");
      setSuccessMsg("");
      setProgress(0);
      setLoading(true);

      try {
        const formData = new FormData();
        formData.append("name", values.name);
        formData.append("mainCategory", values.mainCategory);
        // formData.append("parentCategory", values.parentCategory);
        // formData.append("subCategory", values.subCategory);
        formData.append("file", values.file);
        formData.append("usertype", values.usertype);
        formData.append("userId", values.userId);
        const api= `${process.env.REACT_APP_BASE_URL}api/admin/v1/product/product-import`;
        
        const response = await axios.post(api, formData, {
        headers: {
            Authorization: `Bearer ${authManager.getAccessToken()}`,
            "Content-Type": "multipart/form-data",
        },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setProgress(percentCompleted);
          },
        });

        if (response.data.success) {
          setSuccessMsg(
            `✅ Import successful! Inserted: ${response.data.inserted}, Updated: ${response.data.updated}`
          );
          resetForm();
          setPreview(null);
          setProgress(100);
          callback?.();
         // setTimeout(() => closeModal(), 2000);
        } else {
          setServerError("Import failed. Please check your file.");
        }
      } catch (error) {
        console.error("Error importing file:", error);
        const msg =
          error.response?.data?.message ||
          error.message ||
          "Something went wrong while importing.";
        setServerError(msg);
      } finally {
        setLoading(false);
      }
    },
  });

  // Fetch Main Category
  const { data: categoryData } = useFetch({
    apiMethod: apiRequest.getMainCategory,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
    autoFetch: true,
  });

  // Fetch Admin Head
  const { data: adminList } = useFetch({
    apiMethod: apiRequest.getHeadAdmin,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
    autoFetch: true,
  });

  // Fetch Sub Categories
  const { data: category, refetch: fetchCategory, setBlankResponse } = useFetch({
    apiMethod: apiRequest.getCategory,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
    autoFetch: false,
  });

  const {
    data: subCategory,
    refetch: fetchSubCategory,
    setBlankResponse: subSetBlankResponse,
  } = useFetch({
    apiMethod: apiRequest.getCategory,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
    autoFetch: false,
  });

  useEffect(() => {
    const mainCategory = formik.values.mainCategory;
    if (mainCategory) {
      fetchCategory({ mainCategory, parentCategory: { $eq: null } });
      formik.setFieldValue("parentCategory", "");
    } else {
      setBlankResponse();
    }
  }, [formik.values.mainCategory]);

  useEffect(() => {
    const { mainCategory, parentCategory } = formik.values;
    if (parentCategory && mainCategory) {
      fetchSubCategory({ parentCategory, mainCategory });
      formik.setFieldValue("subCategory", "");
    } else {
      subSetBlankResponse();
    }
  }, [formik.values.parentCategory]);

  return (
    <div>
      <h2 className="p-4 text-lg font-semibold">📦 Import Products</h2>
      <div className="border-b" />

      <form onSubmit={formik.handleSubmit} className="text-sm">
        {serverError && <Alert message={serverError} type="error" />}
        {successMsg && <Alert message={successMsg} type="success" />}

        <div className="p-4 space-y-4">
          <InputField
            name="name"
            label="Excel Name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && formik.errors.name}
            placeholder="Enter Excel Name"
          />

          <SelectField
            name="userId"
            label="Admin Head"
            placeholder="Select Admin Head"
            value={formik.values.userId}
            onChange={formik.handleChange}
            options={adminList?.admins?.map((item) => ({
              value: item._id,
              label: item.name,
            }))}
          />

          <div className="grid grid-cols-2 gap-2">
            <SelectField
              id="mainCategory"
              name="mainCategory"
              label="Main Category"
              options={categoryData?.categories?.map((item) => ({
                value: item._id,
                label: item.name,
              }))}
              value={formik.values.mainCategory}
              onChange={formik.handleChange}
              error={formik.touched.mainCategory && formik.errors.mainCategory}
            />

            {/* <SelectField
              id="parentCategory"
              name="parentCategory"
              label="Category"
              options={category?.categories?.map((item) => ({
                value: item._id,
                label: item.name,
              }))}
              value={formik.values.parentCategory}
              onChange={formik.handleChange}
            />

            <SelectField
              id="subCategory"
              name="subCategory"
              label="Sub Category"
              options={subCategory?.categories?.map((item) => ({
                value: item._id,
                label: item.name,
              }))}
              value={formik.values.subCategory}
              onChange={formik.handleChange}
            /> */}
          </div>

          <FileInputField
            error={formik.touched.file && formik.errors.file}
            id="file"
            label="Upload Excel File"
            accept=".xls,.xlsx"
            onChange={(e) => {
              const file = e.currentTarget.files?.[0] || null;
              formik.setFieldValue("file", file);
              setPreview(file ? file.name : null);
            }}
          />
          {preview && <p className="mt-2 text-gray-600">📄 {preview}</p>}

          {progress > 0 && (
            <div className="w-full bg-gray-200 rounded-full h-3 mt-4">
              <div
                className="bg-blue-500 h-3 rounded-full text-center text-white text-xs"
                style={{ width: `${progress}%` }}
              >
                {progress}%
              </div>
            </div>
          )}
        </div>

        <div className="border-b" />
        <div className="flex justify-end gap-2 p-4">
          <Button variant="secondary" onClick={closeModal} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loadingText="Importing..."
            isLoading={loading}
          >
            Import
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProductImport;
